// Application route tree.
// Authenticated routes are wrapped in ProtectedRoute + AppLayout.
// All page components are lazy-loaded (Suspense boundary is in AppLayout's Outlet).
// Login page has its own Suspense since it renders outside AppLayout.

import { Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import AppLayout from '../shared/components/Layout/AppLayout';
import ProtectedRoute from './protectedRoute';
import LoadingOverlay from '../shared/components/common/LoadingOverlay';
import RouteErrorElement from '../shared/components/common/RouteErrorElement';
import {
  AccountDetailPage,
  AccountsPage,
  AccountStatementPage,
  AcceptInvitePage,
  AdminGroupDetailPage,
  AdminGroupListPage,
  AdminRolesPage,
  AdminAccessPage,
  ClearBankSettingsPage,
  ConnectorHealthPage,
  ConnectorsPage,
  DeadLetterQueuePage,
  IntegrationDashboardPage,
  ReconciliationPage,
  RequestAuditPage,
  TenantFlagsPage,
  WebhookLogPage,
  AdminTenantDetailPage,
  AdminTenantListPage,
  SchemaStudioListPage,
  ImportProfileListPage,
  ImportProfileEditorPage,
  SchemaTypeDetailPage,
  SchemaDraftDesignerPage,
  SchemaObjectsPage,
  ObjectDraftDesignerPage,
  SchemaDictionariesPage,
  AdminUserDetailPage,
  AdminUserListPage,
  ChartOfAccountsEditorPage,
  BalanceCalculationPage,
  FxRatesPage,
  FxPositionPage,
  BalanceSummaryPage,
  GlLedgerPage,
  TransactionDetailPage,
  TransactionTypesPage,
  ManualGlEntryPage,
  CustomerDetailPage,
  CustomerListPage,
  DashboardPage,
  DocumentCreatePage,
  DocumentDetailPage,
  DocumentListPage,
  FeeScheduleDetailPage,
  FeeSimulatorPage,
  LoginPage,
  PricingPlanDetailPage,
  PricingPlansPage,
  ReferenceTableDetailPage,
  ReferenceTablesPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  ChangePasswordPage,
  MfaSignInPage,
  SecurityPage,
  TenantSettingsPage,
  ReportMappingsPage,
  AccountCodeLayerPage,
  CardProductRoutingPage,
  TransactionLimitsPage,
  WorkflowDetailPage,
  WorkflowDraftDesignerPage,
  WorkflowListPage,
  WorkflowPoliciesPage,
  MyAssignmentsPage,
  BalanceSheetPage,
  ProfitAndLossPage,
  TrialBalancePage,
  HoldsReportPage,
  TrialBalanceByAccountPage,
  SafeguardingPage,
  CapitalRequirementsPage,
} from './lazyRoutes';

const router = createBrowserRouter([
  // Public
  {
    path: '/login',
    element: (
      <Suspense fallback={<LoadingOverlay />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/register',
    element: (
      <Suspense fallback={<LoadingOverlay />}>
        <RegisterPage />
      </Suspense>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <Suspense fallback={<LoadingOverlay />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <Suspense fallback={<LoadingOverlay />}>
        <ResetPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/sign-in/verify',
    element: (
      <Suspense fallback={<LoadingOverlay />}>
        <MfaSignInPage />
      </Suspense>
    ),
  },
  {
    path: '/accept-invite',
    element: (
      <Suspense fallback={<LoadingOverlay />}>
        <AcceptInvitePage />
      </Suspense>
    ),
  },

  // Authenticated shell
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // Dashboard
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'account/password', element: <ChangePasswordPage /> },
      { path: 'account/security', element: <SecurityPage /> },

      // Documents
      { path: 'documents', element: <Navigate to="/dashboard" replace /> },
      // Category-level "show all" view. Static "category" segment outranks the
      // dynamic :typeCode route below, so /documents/category/X never collides.
      { path: 'documents/category/:categoryCode', element: <DocumentListPage /> },
      { path: 'documents/:typeCode', element: <DocumentListPage /> },
      { path: 'documents/:typeCode/new', element: <DocumentCreatePage /> },
      { path: 'documents/:typeCode/:id', element: <DocumentDetailPage /> },

      // KYC Onboarding alias (convenience shortcut → same DocumentListPage)
      { path: 'onboarding', element: <Navigate to="/documents/KYC_ONBOARDING" replace /> },

      // Customers
      { path: 'customers', element: <CustomerListPage /> },
      { path: 'customers/:id', element: <CustomerDetailPage /> },

      // GL / Accounts
      // Task 381: the read-only chart duplicated the tree on the Accounts page; 382 put the editor in Configure.
      { path: 'gl/chart-of-accounts', element: <Navigate to="/gl/accounts" replace /> },
      { path: 'gl/accounts', element: <AccountsPage /> },
      { path: 'gl/accounts/:id', element: <AccountDetailPage /> },
      { path: 'gl/accounts/:id/statement', element: <AccountStatementPage /> },
      { path: 'gl/balances', element: <BalanceSummaryPage /> },
      { path: 'gl/ledger', element: <GlLedgerPage /> },
      { path: 'gl/transactions/:id', element: <TransactionDetailPage /> },
      { path: 'gl/transaction-types', element: <TransactionTypesPage /> },
      {
        path: 'gl/manual-entry',
        element: <ManualGlEntryPage />,
        errorElement: <RouteErrorElement />,
      },

      // Tools (Task 208)
      { path: 'tools/balance-calculation', element: <BalanceCalculationPage /> },
      { path: 'tools/fx-rates', element: <FxRatesPage /> },

      // Treasury (task 531, ADR-078)
      { path: 'treasury/fx-position', element: <FxPositionPage /> },

      // Fees
      { path: 'fees/plans', element: <PricingPlansPage /> },
      { path: 'fees/plans/:id', element: <PricingPlanDetailPage /> },
      { path: 'fees/schedules/:id', element: <FeeScheduleDetailPage /> },
      { path: 'fees/simulator', element: <FeeSimulatorPage /> },

      // Workflows
      { path: 'workflows', element: <WorkflowListPage /> },
      { path: 'workflows/:code', element: <WorkflowDetailPage /> },
      { path: 'workflows/:code/draft', element: <WorkflowDraftDesignerPage /> },

      // Assignments (task 402.9). /tasks kept as a redirect: it was in the sidebar for a year.
      { path: 'assignments', element: <MyAssignmentsPage /> },
      { path: 'tasks', element: <Navigate to="/assignments" replace /> },

      // Financial Reports (Task 176)
      { path: 'reports/balance-sheet', element: <BalanceSheetPage /> },
      { path: 'reports/profit-loss', element: <ProfitAndLossPage /> },
      { path: 'reports/trial-balance', element: <TrialBalancePage /> },
      { path: 'reports/holds', element: <HoldsReportPage /> },
      { path: 'reports/trial-balance-accounts', element: <TrialBalanceByAccountPage /> },
      { path: 'reports/safeguarding', element: <SafeguardingPage /> },
      { path: 'reports/capital-requirements', element: <CapitalRequirementsPage /> },

      // Reference Data
      { path: 'reference', element: <ReferenceTablesPage /> },
      { path: 'reference/:tableName', element: <ReferenceTableDetailPage /> },

      // Integration Engine admin (Task 239.25, permission enforced in page)
      { path: 'ops/integration', element: <IntegrationDashboardPage /> },
      { path: 'ops/integration/dlq', element: <DeadLetterQueuePage /> },
      { path: 'ops/integration/connectors', element: <ConnectorsPage /> },
      { path: 'ops/integration/connectors/:id/health', element: <ConnectorHealthPage /> },
      { path: 'ops/integration/requests', element: <RequestAuditPage /> },
      { path: 'ops/integration/webhooks', element: <WebhookLogPage /> },
      { path: 'ops/integration/reconciliation', element: <ReconciliationPage /> },
      { path: 'ops/integration/tenant-flags', element: <TenantFlagsPage /> },
      { path: 'ops/integration/clearbank', element: <ClearBankSettingsPage /> },

      // Admin — Tenant Management (Task 235, permission enforced in page)
      { path: 'admin/schema-studio', element: <SchemaStudioListPage /> },
      // Task 422.9: batch import profiles (global admin, enforced in page and api).
      { path: 'admin/import-profiles', element: <ImportProfileListPage /> },
      { path: 'admin/import-profiles/:profileCode', element: <ImportProfileEditorPage /> },
      // Static segments (objects/dictionaries) outrank the :typeCode param in v6 route ranking.
      { path: 'admin/schema-studio/objects', element: <SchemaObjectsPage /> },
      {
        path: 'admin/schema-studio/objects/:objectTypeCode/draft',
        element: <ObjectDraftDesignerPage />,
      },
      { path: 'admin/schema-studio/dictionaries', element: <SchemaDictionariesPage /> },
      { path: 'admin/schema-studio/:typeCode', element: <SchemaTypeDetailPage /> },
      { path: 'admin/schema-studio/:typeCode/draft', element: <SchemaDraftDesignerPage /> },
      { path: 'admin/tenants', element: <AdminTenantListPage /> },
      { path: 'admin/tenants/:id', element: <AdminTenantDetailPage /> },

      // Admin — User & Group Management (Task 236, permission enforced in page)
      { path: 'admin/chart-of-accounts', element: <ChartOfAccountsEditorPage /> },
      { path: 'admin/users', element: <AdminUserListPage /> },
      { path: 'admin/users/:id', element: <AdminUserDetailPage /> },
      { path: 'admin/groups', element: <AdminGroupListPage /> },
      { path: 'admin/groups/:id', element: <AdminGroupDetailPage /> },
      { path: 'admin/roles', element: <AdminRolesPage /> },
      { path: 'admin/access', element: <AdminAccessPage /> },

      // Settings (admin — permission enforced in page per F29)
      { path: 'settings/tenant', element: <TenantSettingsPage /> },
      { path: 'settings/report-mappings', element: <ReportMappingsPage /> },
      { path: 'settings/account-codes', element: <AccountCodeLayerPage /> },
      { path: 'settings/card-product-routing', element: <CardProductRoutingPage /> },
      { path: 'settings/limits', element: <TransactionLimitsPage /> },
      { path: 'settings/workflow-policies', element: <WorkflowPoliciesPage /> },

      // Catch-all: any unrecognised path inside the authenticated shell → dashboard.
      { path: '*', element: <Navigate to="/dashboard" replace /> },
    ],
  },
]);

export default router;
